import {
  Alert,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import { useState } from 'react'
import {
  usePolicies,
  useRemoveAdvancePolicy,
  useRemoveCancellationPolicy,
} from '@/api/hooks'
import type { AdvancePolicyView, CancellationPolicyView } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { EmptyState, ErrorBanner, humanise } from '@/components/common'
import { AdvancePolicyDialog } from './AdvancePolicyDialog'
import { CancellationPolicyDialog } from './CancellationPolicyDialog'

/**
 * The terms an event is sold on: what calling it off costs, and when the money
 * falls due. Both are days against a percentage of the order value.
 */
export function PoliciesTab() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const { data, error } = usePolicies()
  const removeCancellation = useRemoveCancellationPolicy()
  const removeAdvance = useRemoveAdvancePolicy()
  const [editingCancellation, setEditingCancellation] = useState<CancellationPolicyView | null>(null)
  const [editingAdvance, setEditingAdvance] = useState<AdvancePolicyView | null>(null)
  const [creating, setCreating] = useState<'CANCELLATION' | 'ADVANCE' | null>(null)

  const writable = canWrite('config')

  return (
    <>
      <ErrorBanner error={error ?? removeCancellation.error ?? removeAdvance.error} />

      {/* --------------------------------------------------------- advance */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <div>
          <Typography variant="subtitle1">Advance payment</Typography>
          <Typography variant="caption" color="text.secondary">
            What falls due, and how long before the event
          </Typography>
        </div>
        {writable ? (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCreating('ADVANCE')}>
            New plan
          </Button>
        ) : null}
      </Stack>

      {!data || data.advancePolicies.length === 0 ? (
        <EmptyState message="No payment plan yet. Orders follow the configured advance percentages until one is set." />
      ) : (
        data.advancePolicies.map((policy) => (
          <PolicyBlock
            key={policy.id}
            name={policy.name}
            code={policy.code}
            description={policy.description}
            isDefault={policy.defaultPolicy}
            writable={writable}
            onEdit={() => setEditingAdvance(policy)}
            onRemove={() => removeAdvance.mutate(policy.id)}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Instalment</TableCell>
                  <TableCell align="right">Falls due</TableCell>
                  <TableCell align="right">Share of order</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {policy.slabs.map((slab) => (
                  <TableRow key={slab.id ?? slab.milestone}>
                    <TableCell>{humanise(slab.milestone)}</TableCell>
                    <TableCell align="right">
                      {slab.daysBeforeEvent === 0
                        ? 'On the event day'
                        : `${slab.daysBeforeEvent} day${slab.daysBeforeEvent === 1 ? '' : 's'} before`}
                    </TableCell>
                    <TableCell align="right">{formatPercent(slab.percentOfOrder)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {slab.remarks}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </PolicyBlock>
        ))
      )}

      {/* ---------------------------------------------------- cancellation */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 4, mb: 1 }}>
        <div>
          <Typography variant="subtitle1">Cancellation</Typography>
          <Typography variant="caption" color="text.secondary">
            What the house keeps when an event is called off
          </Typography>
        </div>
        {writable ? (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCreating('CANCELLATION')}>
            New terms
          </Button>
        ) : null}
      </Stack>

      {!data || data.cancellationPolicies.length === 0 ? (
        <EmptyState message="No cancellation terms yet. Cancellations follow the configured slabs until a policy is set." />
      ) : (
        data.cancellationPolicies.map((policy) => (
          <PolicyBlock
            key={policy.id}
            name={policy.name}
            code={policy.code}
            description={policy.description}
            isDefault={policy.defaultPolicy}
            writable={writable}
            onEdit={() => setEditingCancellation(policy)}
            onRemove={() => removeCancellation.mutate(policy.id)}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Notice given</TableCell>
                  <TableCell align="right">Charge</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {noticeBands(policy).map((band) => (
                  <TableRow key={band.key}>
                    <TableCell>{band.label}</TableCell>
                    <TableCell align="right">{formatPercent(band.chargePercent)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {band.remarks}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell>{freeBand(policy)}</TableCell>
                  <TableCell align="right">Nil</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      Beyond the longest slab, nothing is charged
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </PolicyBlock>
        ))
      )}

      <Alert severity="info" sx={{ mt: 3 }}>
        Changing terms applies from here on. An order already cancelled keeps the charge it was
        cancelled on, and a payment schedule already raised is not rebuilt.
      </Alert>

      <CancellationPolicyDialog
        open={creating === 'CANCELLATION' || editingCancellation !== null}
        policy={editingCancellation}
        onClose={() => {
          setCreating(null)
          setEditingCancellation(null)
        }}
      />
      <AdvancePolicyDialog
        open={creating === 'ADVANCE' || editingAdvance !== null}
        policy={editingAdvance}
        onClose={() => {
          setCreating(null)
          setEditingAdvance(null)
        }}
      />
    </>
  )
}

function PolicyBlock({
  name,
  code,
  description,
  isDefault,
  writable,
  onEdit,
  onRemove,
  children,
}: {
  name: string
  code: string
  description?: string
  isDefault: boolean
  writable: boolean
  onEdit: () => void
  onRemove: () => void
  children: React.ReactNode
}) {
  return (
    <div style={{ marginBottom: 24 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="body2">{name}</Typography>
        <Chip size="small" variant="outlined" label={code} />
        {isDefault ? <Chip size="small" color="primary" label="Default" /> : null}
        <div style={{ flexGrow: 1 }} />
        {writable ? (
          <>
            <IconButton size="small" aria-label={`Edit ${name}`} onClick={onEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" aria-label={`Withdraw ${name}`} onClick={onRemove}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </>
        ) : null}
      </Stack>
      {description ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          {description}
        </Typography>
      ) : null}
      {children}
    </div>
  )
}

/**
 * Turns the slabs into the bands they actually charge.
 *
 * <p>A slab is written as "within N days", and the tightest one covering the
 * notice given is the one that applies - so a 90 day slab sitting above a 30 day
 * one really charges notice of 31 to 90 days. Reading the raw numbers gets this
 * wrong often enough to be worth spelling out.</p>
 */
function noticeBands(policy: CancellationPolicyView) {
  const ascending = [...policy.slabs].sort((a, b) => a.daysBeforeEvent - b.daysBeforeEvent)
  return ascending.map((slab, index) => {
    const floor = index === 0 ? null : ascending[index - 1].daysBeforeEvent + 1
    const label =
      slab.daysBeforeEvent === 0
        ? 'On the event day, or after'
        : floor === null
          ? `Up to ${slab.daysBeforeEvent} days`
          : floor === slab.daysBeforeEvent
            ? `${slab.daysBeforeEvent} days`
            : `${floor} to ${slab.daysBeforeEvent} days`
    return { key: String(slab.daysBeforeEvent), label, chargePercent: slab.chargePercent, remarks: slab.remarks }
  })
}

function freeBand(policy: CancellationPolicyView) {
  const longest = policy.slabs.reduce((max, slab) => Math.max(max, slab.daysBeforeEvent), 0)
  return `More than ${longest} days`
}

export function formatPercent(value: number) {
  const rounded = Math.round(value * 1000) / 1000
  return `${rounded}%`
}
